import React from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { AlertCircle, FileX2, Lock, RefreshCw } from "lucide-react";
import DashboardLayout from "@/components/DashboardLayout";

export interface FinanceEmptyStateProps {
  title?: string;
  description?: string;
  icon?: React.ComponentType<{ className?: string }>;
  action?: {
    label: string;
    onClick: () => void;
    icon?: React.ComponentType<{ className?: string }>;
  };
  className?: string;
}

export function FinanceEmptyState({
  title = "No hay datos registrados",
  description = "No se encontraron elementos para la selección actual.",
  icon: Icon = FileX2,
  action,
  className = "",
}: FinanceEmptyStateProps) {
  return (
    <div className={`flex flex-col items-center justify-center rounded-lg border border-dashed border-border/80 bg-muted/10 px-6 py-12 text-center ${className}`}>
      <div className="flex h-12 w-12 items-center justify-center rounded-full bg-muted text-muted-foreground mb-3">
        <Icon className="h-6 w-6" />
      </div>
      <h3 className="text-sm font-semibold text-foreground">{title}</h3>
      <p className="mt-1 max-w-sm text-xs text-muted-foreground leading-relaxed">{description}</p>
      {action && (
        <Button size="sm" onClick={action.onClick} className="mt-4 gap-1.5 font-medium">
          {action.icon && <action.icon className="h-4 w-4" />}
          <span>{action.label}</span>
        </Button>
      )}
    </div>
  );
}

export interface FinanceLoadingStateProps {
  cardsCount?: number;
  showTable?: boolean;
}

export function FinanceLoadingState({ cardsCount = 4, showTable = true }: FinanceLoadingStateProps) {
  const gridColumnsClass =
    cardsCount <= 1
      ? "lg:grid-cols-1"
      : cardsCount === 2
      ? "lg:grid-cols-2"
      : cardsCount === 3
      ? "lg:grid-cols-3"
      : "lg:grid-cols-4";

  return (
    <div className="space-y-6 animate-pulse">
      <div className="space-y-2">
        <Skeleton className="h-4 w-36" />
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-4 w-96" />
      </div>

      <div className={`grid gap-3 sm:grid-cols-2 ${gridColumnsClass}`}>
        {Array.from({ length: cardsCount }).map((_, index) => (
          <Skeleton key={index} className="h-36 rounded-lg" />
        ))}
      </div>

      {showTable && (
        <div className="space-y-3">
          <Skeleton className="h-10 w-full rounded-md" />
          <Skeleton className="h-64 w-full rounded-md" />
        </div>
      )}
    </div>
  );
}

export interface FinanceErrorStateProps {
  title?: string;
  message?: string;
  onRetry?: () => void;
  className?: string;
}

export function FinanceErrorState({
  title = "Error al cargar módulo financiero",
  message = "No se pudieron obtener los datos actualizados.",
  onRetry,
  className = "",
}: FinanceErrorStateProps) {
  return (
    <div className={`flex min-h-[40vh] items-center justify-center p-4 ${className}`}>
      <Card className="max-w-md border-rose-200/80 bg-rose-50/20 shadow-sm dark:border-rose-900/50">
        <CardContent className="flex flex-col items-center gap-3 px-6 py-8 text-center">
          <div className="flex h-12 w-12 items-center justify-center rounded-full bg-rose-100 dark:bg-rose-950/60 text-rose-600">
            <AlertCircle className="h-6 w-6" />
          </div>
          <div>
            <h3 className="text-base font-semibold text-foreground">{title}</h3>
            <p className="mt-1 text-xs text-muted-foreground leading-relaxed">{message}</p>
          </div>
          {onRetry && (
            <Button variant="outline" size="sm" onClick={onRetry} className="mt-2 gap-2 border-rose-200">
              <RefreshCw className="h-3.5 w-3.5 text-rose-600" />
              <span>Reintentar</span>
            </Button>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

export interface FinanceAccessDeniedStateProps {
  title?: string;
  message?: string;
}

export function FinanceAccessDeniedState({
  title = "Acceso no disponible",
  message = "Este espacio de finanzas no está disponible para tu perfil de usuario.",
}: FinanceAccessDeniedStateProps) {
  return (
    <DashboardLayout>
      <div className="flex min-h-[55vh] items-center justify-center p-4">
        <Card className="max-w-md border-dashed shadow-2xs">
          <CardContent className="flex flex-col items-center gap-3 px-6 py-10 text-center">
            <div className="flex h-12 w-12 items-center justify-center rounded-full bg-muted text-muted-foreground">
              <Lock className="h-6 w-6" />
            </div>
            <div>
              <h1 className="text-lg font-semibold text-foreground">{title}</h1>
              <p className="mt-1.5 text-xs text-muted-foreground leading-relaxed">{message}</p>
            </div>
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
}
